'use client'

import type { FC } from 'react'
import { useCallback, useState } from 'react'

import { useModal } from '@circlefin/modal-router/useModal/useModal'
import {
  CWHeader,
  CWHeaderDescription,
  CWHeaderHead,
} from '@features/common.components/ComponentsWeb/Header'
import { CWIcon } from '@features/common.components/ComponentsWeb/Icon'
import {
  CWTabs,
  CWTabsItem,
} from '@features/common.components/ComponentsWeb/Tabs'
import { CWTag } from '@features/common.components/ComponentsWeb/Tag'
import { useFeatureFlag } from '@features/flags.hooks/useFeatureFlag'
import { PlayIcon } from '@features/playground.components/PlayIcon'

import { ApiReferencePage } from '../ApiReferencePage'
import { Playground } from '../Playground/Playground'

import type { ApiReferencePageProps } from '../ApiReferencePage'

export type ApiReferenceMainContentProps = {
  /**
   * The operation data to display.
   */
  operation: ApiReferencePageProps['operation']
}

export const ApiReferenceMainContent: FC<ApiReferenceMainContentProps> = ({
  operation,
}) => {
  const [view, setView] = useState('reference')
  const { router } = useModal()
  const unifiedReferenceEnabled = useFeatureFlag('unifiedReferenceEnabled')

  const handlePlayClick = useCallback(() => {
    if (unifiedReferenceEnabled) {
      setView('playground')
    } else {
      router.push({
        pathname: '/playground/interactive',
        query: { operation: operation },
      })
    }
  }, [operation, router, unifiedReferenceEnabled])

  const handleTabClick = useCallback((id: string) => {
    setView(id)
  }, [])

  return (
    <div>
      <div className="border-b border-neutral-subtle p-6">
        <CWHeader variant="secondary">
          {operation.summary && <CWHeaderHead title={operation.summary} />}
          <CWHeaderDescription>
            <span className="flex items-center space-x-2">
              <CWTag
                className="rounded-md bg-bold-primary text-xs uppercase text-invert font-circular-bold"
                variant="neutral"
              >
                {operation.method}
              </CWTag>
              <span className="text-base">{operation.path}</span>
            </span>
          </CWHeaderDescription>
        </CWHeader>
      </div>
      <div>
        {unifiedReferenceEnabled ? (
          <div className="px-6">
            <CWTabs current={view} variant="underlined-black">
              <CWTabsItem id="reference" onClick={handleTabClick}>
                <div className="flex items-center space-x-1">
                  <span>Reference</span>
                  <CWIcon height={20} name="BookOpenOutline" width={20} />
                </div>
              </CWTabsItem>
              <CWTabsItem id="playground" onClick={handleTabClick}>
                <div className="flex items-center space-x-1">
                  <span>Playground</span>
                  <div className="size-5">
                    <PlayIcon />
                  </div>
                </div>
              </CWTabsItem>
            </CWTabs>
          </div>
        ) : null}
        {view === 'reference' ? (
          <div>
            <ApiReferencePage
              handlePlayClick={handlePlayClick}
              operation={operation}
            />
          </div>
        ) : (
          <div className="">
            <Playground operation={operation} />
          </div>
        )}
      </div>
    </div>
  )
}
